import { TypedEmitter } from 'tiny-typed-emitter'
import { persistent } from './LocalStorage'
import { logger } from './Logger'

const log = logger.createChildLogger({ name: 'CanaryFeatures' })

interface FeatureEvents {
    ready: () => void
}

class CanaryFeatures extends TypedEmitter<FeatureEvents> {
    private features: { [key: string]: boolean } | undefined
    constructor() {
        super()
        this.features = persistent.getObject('canaryFeatures') ?? undefined
        if (this.features) {
            this.emit('ready')
        }
    }

    public update(features: { [key: string]: boolean }) {
        const wasNotReady = this.features === undefined
        this.features = features
        persistent.set('canaryFeatures', features)
        if (wasNotReady) {
            this.emit('ready')
        }
    }

    // Accessing features synchronously is safe in most of the app, because the
    // AuthenticatedApp is not rendered until features are available.
    public getSync(featureId: string): boolean {
        if (!this.features) {
            log.error('isEnabled', `Attempted to check feature "${featureId}" before features are available.`)
            return false
        }

        if (this.features[featureId] === undefined) {
            log.error('isEnabled', `Attempted to check feature "${featureId}" which does not exist.`)
            return false
        }

        return this.features[featureId]
    }

    public get(featureId: string): Promise<boolean> {
        return new Promise((r) => {
            if (this.features) {
                r(this.getSync(featureId))
            } else {
                this.on('ready', () => {
                    r(this.getSync(featureId))
                })
            }
        })
    }

    public get ready(): boolean {
        return !!this.features
    }
}

export enum Features {
    HybridAudioEngine = 'hybrid-audio-engine',
    VirtualBackgrounds = 'virtual-backgrounds',
}

export const canaryFeatures = new CanaryFeatures()
